import { useState, type FormEvent } from 'react';
import { isValidGuestSignInId } from '@kaeliux/shared';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { AUTH_LINK_CLASSES } from './authLinkClasses';

export interface GuestIdSignInFormProps {
    onSubmit: (guestId: string) => Promise<void>;
    disabled?: boolean;
}

/** Lets a guest get back into their game with the guest ID they saved (issue #332). */
export default function GuestIdSignInForm({ onSubmit, disabled = false }: GuestIdSignInFormProps) {
    const [open, setOpen] = useState(false);
    const [guestId, setGuestId] = useState('');
    const [error, setError] = useState<string | undefined>();
    const [submitting, setSubmitting] = useState(false);

    if (!open) {
        return (
            <button type="button" className={AUTH_LINK_CLASSES} disabled={disabled} onClick={() => setOpen(true)}>
                Continue with a guest ID
            </button>
        );
    }

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (!isValidGuestSignInId(guestId)) {
            setError('Enter the 20-character guest ID shown in your guest game.');
            return;
        }
        setError(undefined);
        setSubmitting(true);
        try {
            await onSubmit(guestId);
        } catch {
            // The sign-in page shows why through the shared auth error.
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form noValidate onSubmit={submit} className="flex w-full flex-col gap-2 text-left">
            <Input
                label="Guest ID"
                placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                value={guestId}
                onChange={(e) => setGuestId(e.target.value)}
                error={error}
            />
            <Button type="submit" variant="secondary" loading={submitting} disabled={disabled || submitting}>
                Continue guest game
            </Button>
        </form>
    );
}
